import React, { useState } from "react";
import { Send, Mail, User, Building, Phone, MessageSquare, CheckCircle, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = ({ isVisible }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
    inquiry_type: "general",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterName, setNewsletterName] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data) {
        setIsSubmitted(true);
        toast.success("Message sent successfully! We'll be in touch soon.");
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          subject: "",
          message: "",
          inquiry_type: "general",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, {
        email: newsletterEmail,
        first_name: newsletterName || null,
      });
      if (response.data) {
        setIsSubscribed(true);
        toast.success(
          response.data.status === "already_subscribed"
            ? "You're already subscribed!"
            : "Successfully subscribed to our newsletter!"
        );
        setNewsletterEmail("");
        setNewsletterName("");
      }
    } catch (error) {
      console.error("Newsletter error:", error);
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  const inquiryTypes = [
    { value: "general", label: "General Inquiry" },
    { value: "partnership", label: "Partnership Opportunity" },
    { value: "nonprofit", label: "Nonprofit / Foundation" },
    { value: "travel", label: "Travel Club" },
    { value: "marketing", label: "Marketing Services" },
    { value: "technology", label: "Technology / Ghost Assistant" },
    { value: "financial", label: "Financial Services" },
  ];

  return (
    <section
      id="contact"
      className={`section-fade py-24 sm:py-32 bg-white ${isVisible ? "visible" : ""}`}
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/70 mb-6">
            <span className="w-8 h-px bg-[#D4AF37]" />
            Get Started
            <span className="w-8 h-px bg-[#D4AF37]" />
          </span>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0A192F] tracking-tight font-['Outfit'] mb-6">
            Let's Build{" "}
            <span className="text-[#D4AF37]">Together</span>
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            Whether you're interested in partnering with us, using our services, 
            or supporting our mission — we'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-[#F8FAFC] rounded-2xl p-8 sm:p-10">
              <h3 className="text-2xl font-semibold text-[#0A192F] font-['Outfit'] mb-6">
                Send us a message
              </h3>

              {isSubmitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-[#0A192F] font-['Outfit'] mb-2">
                    Message Sent!
                  </h4>
                  <p className="text-[#475569] mb-6">
                    Thank you for reaching out. We'll respond within 24-48 hours.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0A192F] mb-2">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          required
                          className="pl-11 h-12 border-slate-200 focus:border-[#0A192F] focus:ring-[#0A192F]"
                          data-testid="contact-name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A192F] mb-2">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@company.com"
                          required
                          className="pl-11 h-12 border-slate-200 focus:border-[#0A192F] focus:ring-[#0A192F]"
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#0A192F] mb-2">
                        Company
                      </label>
                      <div className="relative">
                        <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                        <Input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Your Company"
                          className="pl-11 h-12 border-slate-200 focus:border-[#0A192F] focus:ring-[#0A192F]"
                          data-testid="contact-company"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A192F] mb-2">
                        Phone
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1 (555) 000-0000"
                          className="pl-11 h-12 border-slate-200 focus:border-[#0A192F] focus:ring-[#0A192F]"
                          data-testid="contact-phone"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A192F] mb-2">
                      Inquiry Type
                    </label>
                    <select
                      name="inquiry_type"
                      value={formData.inquiry_type}
                      onChange={handleInputChange}
                      className="w-full h-12 px-4 rounded-md border border-slate-200 bg-white text-[#0A192F] focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F] focus:outline-none"
                      data-testid="contact-inquiry-type"
                    >
                      {inquiryTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A192F] mb-2">
                      Subject *
                    </label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="How can we help?"
                      required
                      className="h-12 border-slate-200 focus:border-[#0A192F] focus:ring-[#0A192F]"
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A192F] mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell us more about your inquiry..."
                      required
                      rows={5}
                      className="border-slate-200 focus:border-[#0A192F] focus:ring-[#0A192F] resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-[#0A192F] hover:bg-[#112240] text-white font-semibold"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* Newsletter & Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Newsletter */}
            <div className="bg-[#0A192F] rounded-2xl p-8 text-white">
              <h3 className="text-xl font-semibold font-['Outfit'] mb-3">
                Stay Updated
              </h3>
              <p className="text-white/70 mb-6 text-sm leading-relaxed">
                Subscribe to our newsletter for updates on our initiatives, 
                success stories, and opportunities to get involved.
              </p>

              {isSubscribed ? (
                <div className="text-center py-4" data-testid="newsletter-success">
                  <CheckCircle className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                  <p className="text-[#D4AF37] font-medium">You're subscribed!</p>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="space-y-3" data-testid="newsletter-form">
                  <Input
                    type="text"
                    value={newsletterName}
                    onChange={(e) => setNewsletterName(e.target.value)}
                    placeholder="First name (optional)"
                    className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="newsletter-name"
                  />
                  <Input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="newsletter-email"
                  />
                  <Button
                    type="submit"
                    disabled={isSubscribing}
                    className="w-full h-11 bg-[#D4AF37] hover:bg-[#FBBF24] text-[#0A192F] font-semibold"
                    data-testid="newsletter-submit-button"
                  >
                    {isSubscribing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Subscribe"
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="bg-[#F8FAFC] rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#0A192F] font-['Outfit'] mb-6">
                Other Ways to Connect
              </h3>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#0A192F] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#0A192F]">Email Us</p>
                    <a
                      href="mailto:info@prestigiouscompanies.org"
                      className="text-sm text-[#475569] hover:text-[#D4AF37] transition-colors"
                    >
                      info@prestigiouscompanies.org
                    </a>
                  </div>
                </div>

                </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
